import React, { useEffect, useMemo, useRef, useState, Suspense } from 'react'
import { useGlobalcontext } from '../context';
import { useLoader } from 'react-three-fiber';
import { MTLLoader, OBJLoader } from 'three-stdlib'
import * as THREE from 'three'

const SceneLoading = ({ instanceId }) => {
  const { addLoadingId, removeLoadingId } = useGlobalcontext();

  useEffect(() => {
    addLoadingId(instanceId);
    return () => {
      removeLoadingId(instanceId);
    };
  }, [instanceId, addLoadingId, removeLoadingId]);

  return null;
};

const SceneInner = (props) => {
  const { obj, mtl, position, rotation, hexcolor, name, isTroughSection } = props;
  const ref = useRef();
  const cloneref = useRef();
  const { material, setObjdata, objdata, setShowall, showall, inletuuids, setInletuuids, outletuuids, setOutletuuids } = useGlobalcontext();
  const materials = useLoader(MTLLoader, mtl);
  const objModal = useLoader(OBJLoader, obj, (loader) => {
    materials.preload();
    loader.setMaterials(materials);
  });
  const clonedObj = useMemo(() => {
    const cloned = objModal.clone();
    cloned.traverse((node) => {
      if (node.isMesh) {
        node.userData.isConveyorMesh = true;
        let standardMat;
        if (material === "Stainless Steel" && isTroughSection) {
          // Stainless Steel: same colour family as Carbon Steel but with a more visible tonal shift.
          // Blend 25% toward a neutral cool-grey — clearly lighter/cooler than carbon steel, still realistic.
          const ssColor = new THREE.Color(hexcolor).lerp(new THREE.Color(0.58, 0.60, 0.62), 0.25);
          standardMat = new THREE.MeshStandardMaterial({
            color: ssColor,
            roughness: 0.32,    // noticeably more polished than carbon steel (0.45)
            metalness: 0.62,    // close to carbon steel (0.70) but slightly lower
            envMapIntensity: 0.65, // moderate env reflection for a clean steel look
          });
        } else {
          const roughness = material === "Carbon Steel" ? 0.45 : 0.3;
          const metalness = material === "Carbon Steel" ? 0.7 : 0.6;
          standardMat = new THREE.MeshStandardMaterial({
            color: hexcolor,
            roughness: roughness,
            metalness: metalness,
          });
        }
        node.material = standardMat;
      }
    });
    return cloned;
  }, [objModal, hexcolor, material, isTroughSection]);

  // Clean up materials when clonedObj changes or component unmounts to prevent memory/GPU leaks
  useEffect(() => {
    const currentCloned = clonedObj;
    return () => {
      if (currentCloned) {
        currentCloned.traverse((node) => {
          if (node.isMesh && node.material) {
            if (Array.isArray(node.material)) {
              node.material.forEach((mat) => mat.dispose());
            } else {
              node.material.dispose();
            }
          }
        });
      }
    };
  }, [clonedObj]);

  useEffect(() => {
    clonedObj.visible = true
  }, [showall, clonedObj])

  const onClickSelection = (event) => {
    event.stopPropagation()
    if (clonedObj.visible === true) {
      setObjdata([
        ...objdata,
        clonedObj
      ]);
      clonedObj.traverse((node) => {
        if (node.isMesh) {
          node.userData.isConveyorMesh = true;
          // Dispose of the previous standard material to avoid GPU leaks
          if (node.material) {
            if (Array.isArray(node.material)) {
              node.material.forEach((mat) => mat.dispose());
            } else {
              node.material.dispose();
            }
          }
          const selectMat = new THREE.MeshStandardMaterial({
            color: "#ADD8E6",
            roughness: 0.25,
            metalness: 0.4,
          });
          node.material = selectMat;
        }
      });
      if (name === "inletqty") {
        setInletuuids([
          ...inletuuids,
          clonedObj.uuid
        ])
      }
      if (name === "outletqty") {
        setOutletuuids([
          ...outletuuids,
          clonedObj.uuid
        ])
      }
    }
  };

  return (
    <mesh
      ref={ref}
      position={position}
      rotation={rotation}
      onClick={onClickSelection}
    >
      <primitive ref={cloneref} object={clonedObj} scale={1} />
    </mesh>
  );
}

export const Scene = (props) => {
  const [instanceId] = useState(() => Math.random().toString(36).substring(2, 9));

  return (
    <Suspense fallback={<SceneLoading instanceId={instanceId} />}>
      <SceneInner {...props} />
    </Suspense>
  );
};

export default Scene;